import React, { useMemo, useState } from 'react';
import { authenticatedFetch } from '../services/authService';
import { useAuthenticatedEffect } from '../hooks/useAuthenticatedEffect';

interface WeatherWidgetProps {
	selectedRegion: number | null;
	selectedWezel: number | null;
	hideHeader?: boolean;
}

interface WezelItem {
	id: number;
	nazwa: string;
	region_id: number;
	latitude?: number | null;
	longitude?: number | null;
	lat?: number | null;
	lon?: number | null;
}

interface CurrentWeather {
	temperature: number;
	windspeed: number;
	weathercode: number;
}

interface HourlyWeatherItem {
    time: string;
    temperature: number;
    windspeed?: number;
    weathercode: number;
}

const WARSAW_COORDS = { lat: 52.2297, lon: 21.0122 };

const codeToEmoji = (code: number): string => {
	if ([0].includes(code)) return '☀️';
	if ([1, 2, 3].includes(code)) return '⛅';
	if ([45, 48].includes(code)) return '🌫️';
	if ([51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82].includes(code)) return '🌧️';
	if ([71, 73, 75, 77, 85, 86].includes(code)) return '❄️';
	if ([95, 96, 99].includes(code)) return '⛈️';
	return '🌡️';
};

const WeatherWidget: React.FC<WeatherWidgetProps> = ({ selectedRegion, selectedWezel, hideHeader = false }) => {
	const [displayName, setDisplayName] = useState<string>('Warszawa');
	const [coords, setCoords] = useState<{ lat: number; lon: number }>(WARSAW_COORDS);
	const [weather, setWeather] = useState<CurrentWeather | null>(null);
    const [hourly, setHourly] = useState<HourlyWeatherItem[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const token = useMemo(() => null, []);

	useAuthenticatedEffect(() => {
		let aborted = false;

		const resolveCoords = async (): Promise<{ lat: number; lon: number; name: string }> => {
			// No region or wezel → Warsaw
			if (!selectedRegion && !selectedWezel) {
				return { ...WARSAW_COORDS, name: 'Warszawa' };
			}

			// If we have region (and optionally wezel), fetch wezels list to determine coords
			if (selectedRegion) {
				try {
					const resp = await authenticatedFetch(`/api/wezels?region_id=${selectedRegion}`, { credentials: 'include' });
					if (!resp.ok) throw new Error('Failed to load wezels');
					const data = await resp.json();
					const wezels: WezelItem[] = data.wezels || [];
					let chosen: WezelItem | undefined;
					if (selectedWezel) {
						chosen = wezels.find(w => w.id === selectedWezel);
					} else {
						chosen = wezels[0];
					}
					if (chosen) {
						const lat = (chosen.latitude ?? chosen.lat) ?? null;
						const lon = (chosen.longitude ?? chosen.lon) ?? null;
						if (lat !== null && lon !== null) {
							return { lat, lon, name: chosen.nazwa };
						}
						// Fallback: geocode by name (Open-Meteo Geocoding)
						try {
							const g = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(chosen.nazwa)}&count=1&language=pl&format=json`);
							if (g.ok) {
								const gj = await g.json();
								if (gj && gj.results && gj.results[0]) {
									return { lat: gj.results[0].latitude, lon: gj.results[0].longitude, name: chosen.nazwa };
								}
							}
						} catch {}
						return { ...WARSAW_COORDS, name: chosen.nazwa || 'Warszawa' };
					}
				} catch (e) {
					// ignore and fallback to Warsaw
				}
			}

			return { ...WARSAW_COORDS, name: 'Warszawa' };
		};

		const loadWeather = async () => {
			setLoading(true);
			setError(null);
			setWeather(null);
			try {
				const { lat, lon, name } = await resolveCoords();
				if (aborted) return;
				setCoords({ lat, lon });
				setDisplayName(name);
				const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true&hourly=temperature_2m,weathercode,windspeed_10m&timezone=auto`;
				const r = await fetch(url);
				if (!r.ok) throw new Error('Weather fetch failed');
				const j = await r.json();
				if (j && j.current_weather) {
					const cw: CurrentWeather = {
						temperature: j.current_weather.temperature,
						windspeed: j.current_weather.windspeed,
						weathercode: j.current_weather.weathercode,
					};
					setWeather(cw);
				} else {
					throw new Error('No current weather');
				}
				// Build next hours forecast (next 8 hours)
				const times: string[] | undefined = j?.hourly?.time;
				const temps: number[] | undefined = j?.hourly?.temperature_2m;
				const wcodes: number[] | undefined = j?.hourly?.weathercode;
				const winds: number[] | undefined = j?.hourly?.windspeed_10m;
				if (times && temps && wcodes) {
					const now = Date.now();
					const startIdx = Math.max(0, times.findIndex((t: string) => new Date(t).getTime() >= now));
					const items: HourlyWeatherItem[] = [];
					for (let i = startIdx; i < Math.min(times.length, startIdx + 8); i++) {
						items.push({
							time: times[i],
							temperature: temps[i],
							weathercode: wcodes[i],
							windspeed: winds ? winds[i] : undefined,
						});
					}
					setHourly(items);
				} else {
					setHourly([]);
				}
			} catch (e: any) {
				setError(e?.message || 'Błąd pobierania pogody');
			} finally {
				if (!aborted) setLoading(false);
			}
		};

		loadWeather();
		return () => { aborted = true; };
	}, [selectedRegion, selectedWezel, token]);

	return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0px',
            border: hideHeader ? 'none' : '1px solid var(--border)',
            borderRadius: hideHeader ? '0' : '4px',
            background: 'var(--card)',
            minWidth: 0,
            overflow: 'hidden',
            minHeight: hideHeader ? '160px' : '160px',
            height: hideHeader ? '160px' : '100%'
        }}>
			{!hideHeader && (
				<div style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					padding: '6px 10px',
					borderBottom: '1px solid var(--border)',
					background: 'var(--bg)'
				}}>
					<span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text)' }}>Pogoda</span>
					<span style={{ fontSize: '10px', color: 'var(--muted)' }}>{displayName}</span>
				</div>
			)}
            <div style={{ padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
				{loading && (
					<div style={{ fontSize: '11px', color: 'var(--muted)' }}>Ładowanie...</div>
				)}
				{!loading && error && (
					<div style={{ fontSize: '11px', color: 'var(--error)' }}>{error}</div>
				)}
				{!loading && !error && weather && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ fontSize: '16px' }}>{codeToEmoji(weather.weathercode)}</div>
						<div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                            <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text)' }}>{Math.round(weather.temperature)}°C</span>
                            <span style={{ fontSize: '10px', color: 'var(--muted)' }}>Wiatr: {Math.round(weather.windspeed)} km/h</span>
						</div>
						{hideHeader && (
							<div style={{ fontSize: '10px', color: 'var(--muted)' }}>{displayName}</div>
						)}
					</div>
				)}
                {!loading && !error && hourly.length > 0 && (
                    <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '2px' }}>
                            {hourly.slice(0,6).map((h) => {
                                const hour = new Date(h.time).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
                                return (
                                    <div key={h.time} style={{
                                        border: '1px solid var(--border)',
                                        borderRadius: '4px',
                                        padding: '4px 6px',
                                        minWidth: '64px',
                                        background: 'var(--bg)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '3px'
                                    }}>
                                        <span style={{ fontSize: '10px', color: 'var(--muted)' }}>{hour}</span>
                                        <span style={{ fontSize: '14px' }}>{codeToEmoji(h.weathercode)}</span>
                                        <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text)' }}>{Math.round(h.temperature)}°C</span>
                                        {typeof h.windspeed === 'number' && (
                                            <span style={{ fontSize: '10px', color: 'var(--muted)' }}>{Math.round(h.windspeed)} km/h</span>
                                        )}
                                    </div>
                                );
                            })}
                    </div>
                )}
			</div>
		</div>
	);
};

export default WeatherWidget;


